import React, { CSSProperties, useContext } from "react";
import { AccordionContext, useAccordionToggle } from "react-bootstrap";
import ClickableContainer from "./ClickableContainer";
import { Row, Col } from "react-bootstrap";
import { Tooltip } from "@merge-api/merge-javascript-shared";
import { HeaderPretitle } from "./text/MergeText";

type CollapsableToggleBaseProps = {
  children: React.ReactNode;
  callback?: (eventKey: string) => void;
  eventKey: string;
  additionalButton?: JSX.Element | null;
  style?: CSSProperties;
};

export const PlusMinusToggle = (props: CollapsableToggleBaseProps) => (
  <CollapsableToggle {...props} openedLogo={"fe-minus"} closedLogo={"fe-plus"} />
);
export const ChevronToggle = (props: CollapsableToggleBaseProps) => (
  <CollapsableToggle {...props} openedLogo={"fe-chevron-down"} closedLogo={"fe-chevron-right"} />
);

type CollapsableToggleProps = CollapsableToggleBaseProps & {
  openedLogo: string;
  closedLogo: string;
};

export const CollapsableToggle = ({
  children,
  eventKey,
  callback,
  openedLogo,
  closedLogo,
  additionalButton,
}: CollapsableToggleProps) => {
  const currentEventKey = useContext(AccordionContext);

  const decoratedOnClick = useAccordionToggle(eventKey, () => callback && callback(eventKey));

  const isCurrentEventKey = currentEventKey === eventKey;

  return (
    <ClickableContainer>
      <Row onClick={decoratedOnClick} className={"align-items-center"}>
        <Col>
          <HeaderPretitle>{children}</HeaderPretitle>
        </Col>
        <Col className="col-auto">
          <div className="d-flex">
            {additionalButton}
            {isCurrentEventKey ? (
              <span className={`fe ${openedLogo}`} />
            ) : (
              <span className={`fe ${closedLogo}`} />
            )}
          </div>
        </Col>
      </Row>
    </ClickableContainer>
  );
};

type SettingsButtonToggleProps = {
  callback?: (eventKey: string) => void;
  eventKey: string;
};

export const SettingsButtonToggle = ({ eventKey, callback }: SettingsButtonToggleProps) => {
  const currentEventKey = useContext(AccordionContext);

  const decoratedOnClick = useAccordionToggle(eventKey, () => callback && callback(eventKey));

  const isCurrentEventKey = currentEventKey === eventKey;

  return (
    <ClickableContainer>
      {isCurrentEventKey ? (
        <Tooltip title="Collapse settings">
          <span className="fe fe-minus" onClick={decoratedOnClick} />
        </Tooltip>
      ) : (
        <Tooltip title="View settings">
          <span className="fe fe-plus" onClick={decoratedOnClick} />
        </Tooltip>
      )}
    </ClickableContainer>
  );
};

type NestedPropertiesButtonToggleProps = {
  callback?: (eventKey: string) => void;
  eventKey: string;
};

export const NestedPropertiesButtonToggle = ({
  eventKey,
  callback,
}: NestedPropertiesButtonToggleProps) => {
  const currentEventKey = useContext(AccordionContext);

  const decoratedOnClick = useAccordionToggle(eventKey, () => callback && callback(eventKey));

  const isCurrentEventKey = currentEventKey === eventKey;

  return (
    <ClickableContainer>
      {isCurrentEventKey ? (
        <Tooltip title="Collapse nested properties">
          <span className="fe fe-minus" onClick={decoratedOnClick} />
        </Tooltip>
      ) : (
        <Tooltip title="Expand nested properties">
          <span className="fe fe-plus" onClick={decoratedOnClick} />
        </Tooltip>
      )}
    </ClickableContainer>
  );
};

type ChevronUpDownToggleWithElementProps = {
  children: React.ReactNode;
  afterChildren: React.ReactNode;
  callback?: (eventKey: string) => void;
  eventKey: string;
  style?: CSSProperties;
};

type CollapsableUpDownToggleWithElementProps = CollapsableToggleProps & {
  afterChildren: React.ReactNode;
};

export const ChevronUpDownToggleWithElement = (props: ChevronUpDownToggleWithElementProps) => (
  <CollapsableUpDownToggleWithElement
    {...props}
    openedLogo={"fe-chevron-down"}
    closedLogo={"fe-chevron-up"}
  />
);

export const CollapsableUpDownToggleWithElement = ({
  children,
  afterChildren,
  eventKey,
  callback,
  openedLogo,
  closedLogo,
  style,
}: CollapsableUpDownToggleWithElementProps) => {
  const currentEventKey = useContext(AccordionContext);
  const containerStyle = style ?? {};

  const decoratedOnClick = useAccordionToggle(eventKey, () => callback && callback(eventKey));

  const isCurrentEventKey = currentEventKey === eventKey;

  return (
    <ClickableContainer>
      <Row onClick={decoratedOnClick} className={"align-items-center"} style={containerStyle}>
        <Col style={{ paddingLeft: "24px" }}>
          {isCurrentEventKey ? (
            <Row>
              {children}
              <i className={`fe ${openedLogo}`} />
              {afterChildren}
            </Row>
          ) : (
            <Row>
              {children}
              <i className={`fe ${closedLogo}`} />
              {afterChildren}
            </Row>
          )}
        </Col>
      </Row>
    </ClickableContainer>
  );
};
